import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
    Row,
    Col,
    Card,
    Table,
    Badge,
    Button,
    Modal,
    Form,
    ProgressBar,
    Tab,
    Nav,
    Dropdown
} from "react-bootstrap";
import { FiArrowLeft, FiEdit, FiTrash2, FiUsers, FiCalendar, FiFlag, FiMoreVertical, FiSettings } from 'react-icons/fi';
import axiosInstance from '../../services/AxiosInstance';
import InternalFooter from '../components/InternalFooter/InternalFooter';
import { formatDateToDDMMMYYYY } from '../../utils/dateFormatter';
import './projectdetails.css';
import '../tasks/taskdetails.css';
import swal from "sweetalert";
import { hasFeatureAccess } from '../../utils/featureLock';
import '../Documents/commonstyle.css';

const ProjectDetails = () => {
    const { projectId } = useParams();
    const navigate = useNavigate();


    const [project, setProject] = useState(null);
    const [tasks, setTasks] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tasksLoading, setTasksLoading] = useState(true);
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [newStatus, setNewStatus] = useState('');
    const [statusLoading, setStatusLoading] = useState(false);
    const [userdata, setUserdata] = useState(null);
    const [hasProjectAccess, setHasProjectAccess] = useState(false);
    const [featureLoading, setFeatureLoading] = useState(true);

    // Project status mapping (3 statuses)
    const projectStatusOptions = [
        { value: 1, label: 'Pending' },
        { value: 2, label: 'In Progress' },
        { value: 3, label: 'Completed' }
    ];

    // Task status mapping (5 statuses)
    const taskStatusOptions = [
        { value: 0, label: 'Pending' },
        { value: 1, label: 'In Progress' },
        { value: 2, label: 'Blocked' },
        { value: 3, label: 'Completed' },
        { value: 4, label: 'Paused' }
    ];

    // Project status helpers
    const getProjectStatusLabel = (status) => {
        const statusOption = projectStatusOptions.find(option => option.value === status);
        return statusOption ? statusOption.label : 'Unknown';
    };

    const getProjectStatusBadgeVariant = (status) => {
        switch (status) {
            case 1: return 'secondary';
            case 2: return 'warning';
            case 3: return 'success';
            default: return 'secondary';
        }
    };

    // Task status helpers
    const getTaskStatusLabel = (status) => {
        const statusOption = taskStatusOptions.find(option => option.value === status);
        return statusOption ? statusOption.label : 'Unknown';
    };

    const getTaskStatusBadgeVariant = (status) => {
        switch (status) {
            case 0: return 'secondary';
            case 1: return 'warning';
            case 2: return 'danger';
            case 3: return 'success';
            case 4: return 'info';
            default: return 'secondary';
        }
    };

    // Helper function to get current user data
    const getCurrentUser = () => {
        const userData = localStorage.getItem("user");
        return userData ? JSON.parse(userData) : null;
    };

    // Helper function to check if current user is the project creator
    const isProjectCreator = (project) => {
        const currentUser = getCurrentUser();
        return currentUser && project.created_by === currentUser.id;
    };

    // Helper function to check if current user is a project member
    const isProjectMember = (project) => {
        const currentUser = getCurrentUser();
        if (!currentUser) return false;

        return project.ProjectMembers && project.ProjectMembers.some(
            member => member.CorporateUser && member.CorporateUser.id === currentUser.id
        );
    };

    // Helper function to check if current user is a project moderator
    const isProjectModerator = (project) => {
        const currentUser = getCurrentUser();
        if (!currentUser) return false;

        return project.ProjectModerators && project.ProjectModerators.some(
            moderator => moderator.CorporateUser && moderator.CorporateUser.id === currentUser.id
        );
    };

    // Helper function to check if current user can edit/delete (creator or moderator)
    const canEditOrDelete = (project) => {
        return isProjectCreator(project) || isProjectModerator(project);
    };

    // Helper function to check if current user can change status (creator, moderator, or member)
    const canChangeStatus = (project) => {
        return isProjectCreator(project) || isProjectModerator(project) || isProjectMember(project);
    };

    // Check project access on component mount
    useEffect(() => {
        const checkProjectAccess = async () => {
            try {
                setFeatureLoading(true);
                const projectAccess = await hasFeatureAccess('project');
                setHasProjectAccess(projectAccess);
                
                if (!projectAccess) {
                    setFeatureLoading(false);
                    return;
                }
                
                // Only fetch data if user has access
                setUserdata(JSON.parse(localStorage.getItem("user")));
                if (projectId) {
                    await fetchProjectDetails();
                    await fetchProjectTasks();
                    await fetchProjectSummary();
                }
            } catch (error) {
                console.error('Error checking project access:', error);
                setHasProjectAccess(false);
            } finally {
                setFeatureLoading(false);
            }
        };
        
        checkProjectAccess();
    }, [projectId]);


    // Set page title
    useEffect(() => {
        if (project) {
            document.title = `${project.name} - Project Details`;
        } else {
            document.title = 'Project Details';
        }

        // Cleanup function to reset title when component unmounts
        return () => {
            document.title = 'Task Management';
        };
    }, [project]);

    const fetchProjectDetails = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get(`/project/details/${projectId}`);
            const projectData = response.data.data || response.data;

            // Handle the nested structure that might be returned
            if (projectData && projectData.data) {
                setProject(projectData.data);
            } else {
                setProject(projectData);
            }
        } catch (error) {
            console.error('Error fetching project details:', error);
            setError('Failed to load project details');
            notifyError('Failed to load project details');
        } finally {
            setLoading(false);
        }
    };

    const fetchProjectTasks = async () => {
        try {
            setTasksLoading(true);
            const userData = JSON.parse(localStorage.getItem("user"));
            const params = new URLSearchParams({
                page: 1,
                limit: 50, // Get more tasks for project details
                project_id: projectId,
                CorporateId: parseInt(userData.corporateId)
            });

            const response = await axiosInstance.get(`/task/list?${params.toString()}`);
            const result = response.data;

            // Transform the API data structure to match the expected format
            const formattedTasks = result.tasks.map(task => ({
                id: task.id,
                title: task.title,
                description: task.description,
                project_id: task.project_id,
                status: task.status,
                priority: task.priority,
                start_date: task.start_date,
                due_date: task.due_date,
                created_by: task.created_by,
                created_at: task.created_at,
                updated_at: task.updated_at,
                parent_task: task.parent_task,
                is_repeatable: task.is_repeatable,
                repeat_type: task.repeat_type,
                repeat_until: task.repeat_until,
                delta: task.delta,
                is_assignee_update_allowed: task.is_assignee_update_allowed,

                // Transform dates
                CreatedAt: formatDateToDDMMMYYYY(task.created_at, false),
                StartDate: formatDateToDDMMMYYYY(task.start_date, false),
                EndDate: formatDateToDDMMMYYYY(task.due_date, false),

                // Transform TaskMembers to expected format
                TaskMembers: task.TaskMembers || [],

                // Transform Assignees to expected format
                Assignees: task.Assignees ? task.Assignees.map(assignee => ({
                    CorporateUserId: assignee.CorporateUserId,
                    full_name: assignee.full_name,
                    Email: assignee.Email
                })) : [],

                // Transform Observers to expected format
                Observers: task.Observers ? task.Observers.map(observer => ({
                    CorporateUserId: observer.CorporateUserId,
                    full_name: observer.full_name,
                    Email: observer.Email
                })) : [],

                // Transform Mentions to expected format
                Mentions: task.Mentions || [],

                // Transform Checklists to expected format
                Checklists: task.Checklists || [],

                // Transform Files to expected format
                Files: task.Files || []
            }));

            setTasks([...formattedTasks]);
        } catch (error) {
            console.error('Error fetching project tasks:', error);
            notifyError('Failed to load project tasks');
        } finally {
            setTasksLoading(false);
        }
    };

    const fetchProjectSummary = async () => {
        try {
            setSummaryLoading(true);
            const response = await axiosInstance.get(`/task/summary/${projectId}`);
            const result = response.data;

            // Handle the API response structure: { project_id, summary: { total, pending, inprogress, blocked, completed } }
            const summaryData = result.data.summary || result;
            setSummary(summaryData);
        } catch (error) {
            console.error('Error fetching project summary:', error);
            notifyError('Failed to load project summary');
        } finally {
            setSummaryLoading(false);
        }
    };

    const handleEdit = () => {
        navigate('/task/editproject', {
            state: { projectid: projectId },
        });
    };

    const handleChangeStatus = () => {
        setNewStatus(project.status);
        setShowStatusModal(true);
    };

    const handleStatusChange = async () => {
        if (!newStatus) return;

        setStatusLoading(true);
        try {
            const response = await axiosInstance.put(`/project/change-status/${projectId}`, {
                status: parseInt(newStatus)
            });

            if (response.data) {
                setProject(prev => ({ ...prev, status: parseInt(newStatus) }));

                toast.success("Project status updated successfully!", {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });

                setShowStatusModal(false);
                setNewStatus('');
            }
        } catch (error) {
            console.error('Error updating project status:', error);
            notifyError(error.response?.data?.message || 'Failed to update project status');
        } finally {
            setStatusLoading(false);
        }
    };

    const handleDeleteWithConfirmation = async () => {
        const willDelete = await swal({
            title: "Are you sure?",
            text: "Once deleted, you will not be able to recover this project and all related tasks!",
            icon: "warning",
            buttons: true,
            dangerMode: true,
        });

        if (willDelete) {
            try {
                projectTasks(projectId);
            } catch (err) {
                notifyError("An unexpected error occurred");
                console.error(err);
            }
        }
    };

    const handleDelete = async (projectid) => {
        try {
            const response = await axiosInstance.delete(`/project/delete/${projectid}`)
            const data = response.data;
            console.log(data)
            if (data.message == 'Project deleted successfully') {
                return 'deleted';
            }
            else {
                notifyError(data.message)
            }
        } catch (error) {
            console.error('Delete error:', error);
            notifyError(error)
        }
    };

    const projectTasks = (projectid) => {
        // Fetch tasks for the specific project
        axiosInstance.get(`/project/tasks/${projectid}`)
            .then(async (response) => {
                const tasks = response.data.data;
                if (tasks && tasks.length > 0) {
                    const willDelete = await swal({
                        title: "This project has tasks!",
                        text: `This project has ${tasks.length} task(s). Deleting the project will also delete all associated tasks. Are you sure you want to continue?`,
                        icon: "warning",
                        buttons: true,
                        dangerMode: true,
                    });
                    if (willDelete) {
                        const result = await handleDelete(projectid);
                        if (result === 'deleted') {
                            swal("Deleted successfully!", {
                                icon: "success",
                            });
                            setTimeout(() => {
                                navigate('/task/projectlist');
                            }, 2000);
                        } else {
                            notifyError("Error in deleting");
                        }
                    }
                } else {
                    const result = await handleDelete(projectid);
                    if (result === 'deleted') {
                        swal("Deleted successfully!", {
                            icon: "success",
                        });
                        setTimeout(() => {
                            navigate('/task/projectlist');
                        }, 2000);
                    } else {
                        notifyError("Error in deleting");
                    }
                }
            })
            .catch(error => {
                console.error('Error fetching project tasks:', error);
            });
    };

    const notifyError = (message) => {
        toast.error(`❌ ${message}`, {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
        });
    };

    const handleExportTransactionStatement = async () => {
        try {
            const rememberMe = localStorage.getItem('rememberMe') === 'true';
            const rawToken = rememberMe ? localStorage.getItem('accessToken') : sessionStorage.getItem('accessToken') || localStorage.getItem('tempAccessToken');
            const token = rawToken ? JSON.parse(rawToken) : null;
            const baseUrl = process.env.REACT_APP_API_URL || '';
            const url = `${baseUrl}/project/comments/${projectId}/export-xlsx`;
            const headers = {};
            if (token) headers.Authorization = `Bearer ${token}`;
            const res = await fetch(url, { method: 'GET', headers });
            if (!res.ok) throw new Error('Export failed');
            const blob = await res.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = downloadUrl;
            const projectName = (project?.name || 'Project').replace(/[^a-zA-Z0-9_-]/g, '_');
            a.download = `Project_Comments_${projectName}_${projectId}.xlsx`;
            a.click();
            window.URL.revokeObjectURL(downloadUrl);
            toast.success('Transaction statement exported successfully', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        } catch (err) {
            console.error('Export error:', err);
            notifyError(err.response?.data?.message || 'Failed to export transaction statement');
        }
    };

    const calculateProgress = () => {
        if (!tasks || !tasks.length) return 0;
        const completedTasks = tasks.filter(task => task.status === 3).length;
        return Math.round((completedTasks / tasks.length) * 100);
    };

    const getTaskStatusCounts = () => {
        if (!tasks || !tasks.length) {
            return {
                pending: 0,
                inProgress: 0,
                completed: 0,
                blocked: 0,
                paused: 0
            };
        }
        const counts = {
            pending: tasks.filter(task => task.status === 0).length,
            inProgress: tasks.filter(task => task.status === 1).length,
            completed: tasks.filter(task => task.status === 3).length,
            blocked: tasks.filter(task => task.status === 2).length,
            paused: tasks.filter(task => task.status === 4).length
        };
        return counts;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'Not set';
        return formatDateToDDMMMYYYY(dateStr);
    };


    // Show loading screen while checking feature access
    if (featureLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    // Show feature locked message if user doesn't have project access
    if (!hasProjectAccess) {
        return (
            <div style={{ padding: '2rem', maxWidth: '800px', margin: '0 auto' }}>
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-12">
                            <div className="card">
                                <div className="card-body text-center">
                                    <div className="feature-locked">
                                        <i className="fas fa-lock" style={{ fontSize: '3rem', marginBottom: '1rem', color: '#c42126' }} aria-hidden="true"></i>
                                        <h4 className="text-muted mb-3">Project Management Not Available</h4>
                                        <p className="text-muted mb-4">
                                            Project management is not available in your current subscription plan.
                                            Upgrade your plan to access this feature.
                                        </p>
                                        <div className="d-flex flex-wrap gap-3 justify-content-center align-items-center">
                                            <button
                                                type="button"
                                                className="btn doc-form-cancel-btn"
                                                onClick={() => navigate('/task/projectlist')}
                                            >
                                                <i className="fas fa-arrow-left me-2" aria-hidden="true"></i>
                                                Back to Project List
                                            </button>
                                            <button
                                                type="button"
                                                className="btn doc-form-submit-btn"
                                                onClick={() => navigate('/user-subscription')}
                                            >
                                                <i className="fas fa-arrow-right me-2" aria-hidden="true"></i>
                                                Upgrade Plan
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
                <div className="text-center">
                    <h5 className="text-muted mb-3">Error loading project</h5>
                    <p className="text-muted mb-3">{error}</p>
                    <Button variant="primary" onClick={() => navigate('/task/projectlist')}>
                        Back to Projects
                    </Button>
                </div>
            </div>
        );
    }

    if (!project) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
                <div className="text-center">
                    <h5 className="text-muted mb-3">Project not found</h5>
                    <p className="text-muted mb-3">Project ID: {projectId}</p>
                    <Button variant="primary" onClick={() => navigate('/task/projectlist')}>
                        Back to Projects
                    </Button>
                </div>
            </div>
        );
    }

    // Use summary data if available, otherwise fallback to calculated values
    const taskCounts = summary ? {
        pending: summary.pending || 0,
        inProgress: summary.inprogress || 0,
        completed: summary.completed || 0,
        blocked: summary.blocked || 0,
        paused: 0
    } : getTaskStatusCounts();

    const progress = summary ? (summary.total > 0 ? Math.round((summary.completed / summary.total) * 100) : 0) : calculateProgress();

    return (
        <div className="project-details-container">
            <ToastContainer />

            {/* Back Button */}
            <button
                className="back-button"
                onClick={() => {
                    if (window.history.length > 2) {
                        navigate(-1);
                    } else {
                        navigate('/task/projectlist');
                    }
                }}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    background: 'none',
                    border: 'none',
                    color: '#C42126',
                    fontSize: '1rem',
                    cursor: 'pointer',
                    marginBottom: '1rem',
                }}
            >
                <FiArrowLeft style={{ marginRight: '0.5rem' }} />
                Back
            </button>

            <Row>
                {/* Main Content */}
                <Col xl={8} xxl={8}>
                    {/* Project Header Card */}
                    <Card className="project-header-card">
                        <Card.Header className="project-header">
                            <div className="d-flex align-items-start justify-content-between" style={{ width: '100%' }}>
                                <div className="d-flex align-items-center">
                                    <div className="ms-3 project-title-container">
                                        <h4 className="project-title mb-1">{project?.name || 'Untitled Project'}</h4>
                                        <Badge bg={getProjectStatusBadgeVariant(project?.status)} className="status-badge-project">
                                            {getProjectStatusLabel(project?.status)}
                                        </Badge>
                                    </div>
                                </div>
                                <div className="project-actions">
                                    <Dropdown>
                                        <Dropdown.Toggle
                                            variant="link"
                                            size="lg"
                                            className="project-actions-dropdown-icon"
                                        >
                                            <FiMoreVertical />
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu align="end">
                                            {canChangeStatus(project) && (
                                                <Dropdown.Item onClick={handleChangeStatus}>
                                                    <FiSettings className="me-2" />
                                                    Change Status
                                                </Dropdown.Item>
                                            )}
                                            {canEditOrDelete(project) && (
                                                <>
                                                    <Dropdown.Item onClick={handleEdit}>
                                                        <FiEdit className="me-2" />
                                                        Edit Project
                                                    </Dropdown.Item>
                                                    <Dropdown.Divider />
                                                    <Dropdown.Item
                                                        onClick={handleDeleteWithConfirmation}
                                                        className="text-danger"
                                                    >
                                                        <FiTrash2 className="me-2" />
                                                        Delete Project
                                                    </Dropdown.Item>
                                                </>
                                            )}
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </div>
                            </div>
                        </Card.Header>
                        <Card.Body className="py-2">
                            {project?.description && (
                                <div className="project-description mb-3">
                                    <h6 className="mb-2">Description</h6>
                                    <p className="text-muted mb-0" style={{ fontSize: '0.9rem', lineHeight: '1.4' }}>
                                        {project.description.length > 150 ?
                                            `${project.description.substring(0, 150)}...` :
                                            project.description
                                        }
                                    </p>
                                </div>
                            )}

                            {/* Ultra Compact Project Stats */}
                            <h6 className="mb-2">Task Summary</h6>
                            <Row className="mb-2">
                                <Col md={2} className="mb-1">
                                    <div className="stat-card-ultra-compact">
                                        <div className="stat-number-ultra-compact">
                                            {summaryLoading ? (
                                                <div className="spinner-border spinner-border-sm" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                            ) : (
                                                summary ? (summary.total || tasks.length) : tasks.length
                                            )}
                                        </div>
                                        <div className="stat-label-ultra-compact">Total</div>
                                    </div>
                                </Col>
                                <Col md={2} className="mb-1">
                                    <div className="stat-card-ultra-compact">
                                        <div className="stat-number-ultra-compact">
                                            {summaryLoading ? (
                                                <div className="spinner-border spinner-border-sm" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                            ) : (
                                                taskCounts.pending
                                            )}
                                        </div>
                                        <div className="stat-label-ultra-compact">Pending</div>
                                    </div>
                                </Col>
                                <Col md={2} className="mb-1">
                                    <div className="stat-card-ultra-compact">
                                        <div className="stat-number-ultra-compact">
                                            {summaryLoading ? (
                                                <div className="spinner-border spinner-border-sm" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                            ) : (
                                                taskCounts.inProgress
                                            )}
                                        </div>
                                        <div className="stat-label-ultra-compact">In Progress</div>
                                    </div>
                                </Col>
                                <Col md={2} className="mb-1">
                                    <div className="stat-card-ultra-compact">
                                        <div className="stat-number-ultra-compact">
                                            {summaryLoading ? (
                                                <div className="spinner-border spinner-border-sm" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                            ) : (
                                                taskCounts.completed
                                            )}
                                        </div>
                                        <div className="stat-label-ultra-compact">Completed</div>
                                    </div>
                                </Col>
                                <Col md={2} className="mb-1">
                                    <div className="stat-card-ultra-compact">
                                        <div className="stat-number-ultra-compact">
                                            {summaryLoading ? (
                                                <div className="spinner-border spinner-border-sm" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                            ) : (
                                                taskCounts.paused
                                            )}
                                        </div>
                                        <div className="stat-label-ultra-compact">Paused</div>
                                    </div>
                                </Col>
                                <Col md={2} className="mb-1">
                                    <div className="stat-card-ultra-compact">
                                        <div className="stat-number-ultra-compact">
                                            {summaryLoading ? (
                                                <div className="spinner-border spinner-border-sm" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                            ) : (
                                                `${progress}%`
                                            )}
                                        </div>
                                        <div className="stat-label-ultra-compact">Progress</div>
                                    </div>
                                </Col>
                            </Row>

                            {/* Ultra Compact Progress Bar */}
                            <div className="progress-section-ultra-compact">
                                <div className="d-flex justify-content-between align-items-center mb-1">
                                    <span className="progress-label-ultra-compact">Progress</span>
                                    <span className="progress-percentage-ultra-compact">
                                        {summaryLoading ? (
                                            <div className="spinner-border spinner-border-sm" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                        ) : (
                                            `${progress}%`
                                        )}
                                    </span>
                                </div>
                                <ProgressBar
                                    now={summaryLoading ? 0 : progress}
                                    variant="success"
                                    className="project-progress-bar-ultra-compact"
                                    style={{ height: '4px' }}
                                />
                            </div>
                        </Card.Body>
                    </Card>

                    {/* Tasks Section */}
                    <Card className="tasks-card">
                        <Card.Header className="d-flex justify-content-between">
                            <h5 className="mb-0">Project Tasks</h5>
                            <div className="d-flex gap-2">
                                <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={() => {
                                        const branchIds = (project?.ProjectBranches || []).map(pb => pb.Branch?.id ?? pb.branch_id).filter(Boolean);
                                        const departmentIds = (project?.ProjectDepartments || []).map(pd => pd.Department?.id ?? pd.department_id).filter(Boolean);
                                        const subDepartmentIds = (project?.ProjectSubDepartments || []).map(psd => psd.SubDepartment?.id ?? psd.sub_department_id).filter(Boolean);
                                        navigate(`/task/addTask?projectId=${projectId}`, {
                                            state: { prefillProject: { BranchId: branchIds, DepartmentId: departmentIds, SubDepartmentId: subDepartmentIds } },
                                        });
                                    }}
                                >
                                    <FiEdit className="me-1" />
                                    Create Task
                                </Button>
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => navigate(`/task/tasklist?projectId=${projectId}`)}
                                >
                                    View All Tasks
                                </Button>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            {tasksLoading ? (
                                <div className="d-flex justify-content-center align-items-center py-4">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading tasks...</span>
                                    </div>
                                </div>
                            ) : tasks.length > 0 ? (
                                <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                    <Table responsive>
                                        <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 10 }}>
                                            <tr>
                                                <th>Task Name</th>
                                                <th>Status</th>
                                                <th>Priority</th>
                                                <th>Due Date</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {tasks.map((task) => (
                                                <tr key={task.id}>
                                                    <td>
                                                        <a
                                                            href={`/task/taskdetails/${task.id}`}
                                                            className="task-link"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                navigate(`/task/taskdetails/${task.id}`);
                                                            }}
                                                        >
                                                            {task.title}
                                                        </a>
                                                    </td>
                                                    <td>
                                                        <Badge bg={getTaskStatusBadgeVariant(task.status)}>
                                                            {getTaskStatusLabel(task.status)}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        <span style={{ fontSize: '0.8rem' }}>
                                                            {task.priority}
                                                        </span>
                                                    </td>
                                                    <td style={{ fontSize: '0.8rem' }}>{formatDate(task.due_date)}</td>
                                                    <td style={{ cursor: 'pointer', textAlign: 'center' }}>
                                                        <FiEdit size={14} onClick={() => navigate(`/task/edittask`, {
                                                            state: { taskid: task.id },
                                                        })} />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            ) : (
                                <div className="text-center py-4">
                                    <FiFlag className="text-muted mb-3" style={{ fontSize: '3rem' }} />
                                    <h6 className="text-muted">No tasks in this project</h6>
                                    <p className="text-muted mb-4">Tasks will appear here once they are created for this project.</p>
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        onClick={() => {
                                            const branchIds = (project?.ProjectBranches || []).map(pb => pb.Branch?.id ?? pb.branch_id).filter(Boolean);
                                            const departmentIds = (project?.ProjectDepartments || []).map(pd => pd.Department?.id ?? pd.department_id).filter(Boolean);
                                            const subDepartmentIds = (project?.ProjectSubDepartments || []).map(psd => psd.SubDepartment?.id ?? psd.sub_department_id).filter(Boolean);
                                            navigate(`/task/addTask?projectId=${projectId}`, {
                                                state: { prefillProject: { BranchId: branchIds, DepartmentId: departmentIds, SubDepartmentId: subDepartmentIds } },
                                            });
                                        }}
                                    >
                                        <FiEdit className="me-1" />
                                        Create First Task
                                    </Button>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>

                {/* Sidebar - task details style: status-box + white fixed container */}
                <Col xl={4} xxl={4} className="task-details-right-column">
                    <div className="status-box">
                        {getProjectStatusLabel(project?.status)}
                    </div>
                    <div className="flex flex-col p-4 w-80 bg-white shadow-lg border-l h-full fixed top-0 right-0 overflow-y-auto">
                        <div className="mb-4 sidebar">
                            <p className="text-sm text-gray-600 font-semibold alignments">Created on:</p>
                            <p className="text-gray-700">{formatDate(project?.created_at)}</p>
                        </div>
                        <div className="mb-4 sidebar">
                            <p className="text-sm text-gray-600 font-semibold alignments">Start Date:</p>
                            <p className="text-gray-700">{formatDate(project?.start_date)}</p>
                        </div>
                        <div className="mb-4 sidebar">
                            <p className="text-sm text-gray-600 font-semibold alignments">End Date:</p>
                            <p className="text-gray-700">{formatDate(project?.end_date)}</p>
                        </div>
                        {project?.actual_completion_date && (
                            <div className="mb-4 sidebar">
                                <p className="text-sm text-gray-600 font-semibold alignments">Completed on:</p>
                                <p className="text-gray-700">{formatDate(project.actual_completion_date)}</p>
                            </div>
                        )}
                        <div className="mb-4 sidebar">
                            <p className="text-sm text-gray-600 font-semibold alignments">Priority:</p>
                            <p className="text-gray-700">{project?.priority || '—'}</p>
                        </div>
                        <div className="mb-4 sidebar">
                            <p className="text-sm text-gray-600 font-semibold alignments">Owner:</p>
                            <p className="text-gray-700">{project?.owner?.full_name || '—'}</p>
                        </div>
                        <div className="mb-4 sidebar">
                            <p className="text-sm text-gray-600 font-semibold alignments">Created by:</p>
                            <p className="text-gray-700">{project?.creator?.full_name || '—'}</p>
                        </div>

                        <hr className="my-4 border-gray-300" />
                        <div className="mb-4">
                            <p className="text-sm text-gray-600 font-semibold mb-2" style={{ fontSize: '0.875rem', color: '#666', fontWeight: '600' }}>Members</p>
                            <div className="user-chip-list">
                                {project?.ProjectMembers && project.ProjectMembers.length > 0 ? (
                                    project.ProjectMembers.map((member, index) => (
                                        <div className="user-chip" key={`member-${index}`}>
                                            <span className="user-chip-avatar">
                                                {member.CorporateUser?.full_name ? member.CorporateUser.full_name[0] : '?'}
                                            </span>
                                            {member.CorporateUser?.full_name || 'Unknown User'}
                                        </div>
                                    ))
                                ) : (
                                    <span style={{ color: '#888', fontStyle: 'italic', paddingLeft: '8px' }}>No members assigned</span>
                                )}
                            </div>
                        </div>
                        <div className="mb-4">
                            <p className="text-sm text-gray-600 font-semibold mb-2" style={{ fontSize: '0.875rem', color: '#666', fontWeight: '600' }}>Moderators</p>
                            <div className="user-chip-list">
                                {project?.ProjectModerators && project.ProjectModerators.length > 0 ? (
                                    project.ProjectModerators.map((moderator, index) => (
                                        <div className="user-chip" key={`mod-${index}`}>
                                            <span className="user-chip-avatar">
                                                {moderator.CorporateUser?.full_name ? moderator.CorporateUser.full_name[0] : '?'}
                                            </span>
                                            {moderator.CorporateUser?.full_name || 'Unknown User'}
                                        </div>
                                    ))
                                ) : (
                                    <span style={{ color: '#888', fontStyle: 'italic', paddingLeft: '8px' }}>No moderators assigned</span>
                                )}
                            </div>
                        </div>
                        <div className="mb-4">
                            <p className="mb-2" style={{ fontSize: '0.875rem', color: '#666', fontWeight: '600' }}>Branches</p>
                            <div className="user-chip-list">
                                {project?.ProjectBranches && project.ProjectBranches.length > 0 ? (
                                    project.ProjectBranches.map((pb, index) => (
                                        <div className="user-chip" key={`branch-${pb.Branch?.id ?? index}`}>
                                            <span className="user-chip-avatar">
                                                <i className="fa fa-sitemap"></i>
                                            </span>
                                            {pb.Branch?.name || '—'}
                                        </div>
                                    ))
                                ) : (
                                    <span style={{ color: '#888', fontStyle: 'italic', paddingLeft: '8px' }}>No branches assigned</span>
                                )}
                            </div>
                        </div>
                        <div className="mb-4">
                            <p className="mb-2" style={{ fontSize: '0.875rem', color: '#666', fontWeight: '600' }}>Departments</p>
                            <div className="user-chip-list">
                                {project?.ProjectDepartments && project.ProjectDepartments.length > 0 ? (
                                    project.ProjectDepartments.map((pd, index) => (
                                        <div className="user-chip" key={`dept-${pd.Department?.id ?? index}`}>
                                            <span className="user-chip-avatar">
                                                <i className="fa fa-building"></i>
                                            </span>
                                            {pd.Department?.name || '—'}
                                        </div>
                                    ))
                                ) : (
                                    <span style={{ color: '#888', fontStyle: 'italic', paddingLeft: '8px' }}>No departments assigned</span>
                                )}
                            </div>
                        </div>
                        <div className="mb-4">
                            <p className="mb-2" style={{ fontSize: '0.875rem', color: '#666', fontWeight: '600' }}>Sub-departments</p>
                            <div className="user-chip-list">
                                {project?.ProjectSubDepartments && project.ProjectSubDepartments.length > 0 ? (
                                    project.ProjectSubDepartments.map((psd, index) => (
                                        <div className="user-chip" key={`subdept-${psd.SubDepartment?.id ?? index}`}>
                                            <span className="user-chip-avatar">
                                                <i className="fa fa-building"></i>
                                            </span>
                                            {psd.SubDepartment?.name || '—'}
                                        </div>
                                    ))
                                ) : (
                                    <span style={{ color: '#888', fontStyle: 'italic', paddingLeft: '8px' }}>No sub-departments assigned</span>
                                )}
                            </div>
                        </div>
                    </div>
                    {/* Transaction Statement Button - matches task details style */}
                    <div className="mt-3 text-center">
                        <Link
                            to={`/task/projectdetails/${projectId}/daily-updates`}
                            className="btn btn-sm btn-transaction-statement btn-daily-updates-action"
                            title="View transaction statement (daily updates)"
                        >
                            Transaction Statement
                        </Link>
                    </div>
                </Col>
            </Row>

            {/* Change Status Modal */}
            <Modal
                show={showStatusModal}
                onHide={() => setShowStatusModal(false)}
                centered
                className="status-change-modal"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Change Project Status</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div>
                        <p><strong>Project:</strong> {project.name}</p>
                        <p><strong>Current Status:</strong> {getProjectStatusLabel(project.status)}</p>
                        <div className="form-group mb-3">
                            <label className="text-label"><strong>New Status*</strong></label>
                            <select
                                className="form-control"
                                value={newStatus}
                                onChange={(e) => setNewStatus(e.target.value)}
                                disabled={statusLoading}
                            >
                                <option value="">Select Status</option>
                                {projectStatusOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <div className="status-modal-footer">
                        <button
                            type="button"
                            className="btn btn-cancel"
                            onClick={() => setShowStatusModal(false)}
                            disabled={statusLoading}
                            style={{ minWidth: '7rem', height: '38px' }}
                        >
                            Cancel
                        </button>
                        <button
                            className="btn btn-primary sw-btn-next"
                            type="button"
                            onClick={handleStatusChange}
                            disabled={!newStatus || statusLoading}
                            style={{ minWidth: '7rem', height: '38px' }}
                        >
                            {statusLoading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Updating...
                                </>
                            ) : (
                                'Update Status'
                            )}
                        </button>
                    </div>
                </Modal.Footer>
            </Modal>


            <InternalFooter />
        </div>
    );
};

export default ProjectDetails;
